import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router";
import SearchInput from "../../../components/shared/SearchInput";
import { globalFilter } from "../../../utils/arrays";
import { AssociatedCompany } from "../../../types/UserDetailModel";
import {
  useAuthStore,
  useCompanyStore,
  usePermissionStore,
} from "../../../store";
import {
  queryClient,
  useGetUserCompanies,
  useGetUserCompanyDetails,
  useGetUserRoleAndPermission,
  useSwitchCompany,
} from "../../../queries";
import { Permission } from "../../../utils/interfaces/Companies";
import { useGrantDetailsStore } from "../../../store/useGrantDetailsStore";
import { useOrgTemplateStore } from "../../../store/useOrgTemplateStore";

export type CompanyType = {
  name: string;
  dba: string;
  id: string;
};
export const SwitchCompanyDropdown: React.FC<any> = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [loading, setLoading] = useState(false);

  const { setAuth, user, clear } = useAuthStore();
  const navigate = useNavigate();

  const pathname = useLocation().pathname;

  const { companyData, setCompanyData } = useCompanyStore();

  const { data: userCompanies, isLoading: companiesLoading } =
    useGetUserCompanies();

  const filteredCompanies = React.useMemo(() => {
    if (userCompanies?.associatedCompanies.length === 0) return [];
    const filterResult = globalFilter(
      userCompanies?.associatedCompanies || [],
      searchTerm,
      ["name", "operatingAs"]
    );
    return filterResult;
  }, [searchTerm, userCompanies?.associatedCompanies]);

  const { mutate: switchUserCompany } = useSwitchCompany();
  const { data, refetch: getPermission } = useGetUserRoleAndPermission();
  const { setPermission } = usePermissionStore();
  const { data: currentCompanyDetails, refetch: refetchUserCompanyDetails } =
    useGetUserCompanyDetails();

  const { clear: grantDataClear } = useGrantDetailsStore();
  const { clear: orgDataClear } = useOrgTemplateStore();

  async function switchCompany(companyId: string) {
    if (companyData?.id === companyId) return;
    if (companyId === userCompanies?.defaultCompany?.id) return;
    if (pathname.includes("options")) {
      navigate("/options/esopOverview");
    }
    setLoading(true);
    sessionStorage.setItem("companyId", companyId.toString());
    const switchCompanyObject = { companyId };
    switchUserCompany(switchCompanyObject, {
      onSuccess: (data) => {
        getPermission().then((permission) => {
          setPermission(permission.data as Permission);
        });
        refetchUserCompanyDetails();
        setLoading(false);
        setIsOpen(false);
      },
      onError: (error) => {
        toast("Error in switching company", {
          type: "error",
          autoClose: 2000,
        });
        setLoading(false);
      },
    });
    queryClient.invalidateQueries();
    queryClient.refetchQueries();
    grantDataClear();
    orgDataClear();
  }

  useEffect(() => {
    function handleClickOutside(event: any) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [setIsOpen]);

  React.useEffect(() => {
    if (currentCompanyDetails) {
      setCompanyData(currentCompanyDetails);
    }
  }, [currentCompanyDetails]);

  return (
    <>
      <div>
        {loading ? (
          <div className="items-center px-6 py-2">
            <p className="text-sm animate-pulse font-medium">Switching...</p>
          </div>
        ) : (
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`bg-gray-100 px-4 py-7 h-10 md:w-64 text-sm border-b  flex items-center justify-between ${
                companyData?.name ? "border-primary" : ""
              }`}
              type="button"
            >
              <span className="text-gray-700 sm:block hidden whitespace-nowrap">
                <div className="w-full max-w-[200px] overflow-hidden">
                  <p
                    className="text-start font-semibold truncate overflow-hidden whitespace-nowrap"
                    title={companyData?.name}
                  >
                    {companyData?.name}
                  </p>
                  <p className="w-24 text-xs1 font-semi-medium text-gray-light whitespace-nowrap text-ellipsis overflow-clip text-start">
                    {companyData?.companyMetaData?.typeOfTheCompany === "PUBLIC"
                      ? `Public Listed`
                      : companyData?.companyMetaData?.typeOfTheCompany ===
                        "PUBLIC_UNLISTED"
                      ? `Public Unlisted`
                      : `Private`}
                  </p>
                </div>
              </span>

              <svg
                className={`w-5 h-5 transition-transform ${
                  isOpen ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {isOpen && (
              <>
                <div className="absolute top-full -right-16 md:right-0 w-64 bg-white rounded-sm shadow-lg z-10 px-3 py-2">
                  <div className="mb-2">
                    <SearchInput
                      placeholder="Search Company"
                      value={searchTerm}
                      onChange={(e: any) => setSearchTerm(e.target.value)}
                    />
                  </div>

                  <div className="max-h-80 overflow-y-auto scroll-smooth">
                    {filteredCompanies.map((company, index) => (
                      <div
                        key={company.id}
                        className={`p-2 px-1 hover:bg-gray-100 ${
                          index !== filteredCompanies.length - 1
                            ? "border-b border-gray-200 border-dashed"
                            : ""
                        } ${
                          companyData?.id === company.id
                            ? "text-gray-300  cursor-not-allowed"
                            : "cursor-pointer"
                        }`}
                        onClick={() => switchCompany(company.id)}
                      >
                        <p
                          className={`${
                            companyData?.id === company.id
                              ? "text-gray-400"
                              : "text-gray-700"
                          }`}
                        >
                          {company.name}
                        </p>
                        {company.operatingAs &&
                          company.operatingAs !== "null" &&
                          company.operatingAs?.toLowerCase() !==
                            company.name?.toLowerCase() && (
                            <p
                              className={`${
                                companyData?.id === company.id
                                  ? "text-gray-300"
                                  : "text-gray-500"
                              } text-xxs`}
                            >
                              {company.operatingAs}
                            </p>
                          )}
                      </div>
                    ))}
                    {filteredCompanies.length === 0 && (
                      <label className="flex items-center px-4 py-2 hover:bg-gray-100 cursor-pointer">
                        <span className="text-gray-700 text-sm">
                          {"No Companies Available"}
                        </span>
                      </label>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
};
